import type { ReactElement } from 'react'
import Markdown, { type Components } from 'react-markdown'

/**
 * Props for {@link MarkdownPreviewView}.
 *
 * @property markdown - The body to render (the TO-97 Markdown subset).
 * @property label - Accessible name of the preview region.
 */
export interface MarkdownPreviewViewProps {
	markdown: string
	label: string
}

interface TreeNode {
	type: string
	value?: string
	children?: TreeNode[]
}

/** Raw HTML becomes literal text: the author sees the markup, the browser never renders it. */
function htmlAsText() {
	const visit = (node: TreeNode) => {
		if (node.type === 'html') node.type = 'text'
		node.children?.forEach(visit)
	}
	return (tree: TreeNode) => visit(tree)
}

const SAFE_SCHEME = /^(https?:|mailto:)/i

/** Keep `http`, `https` and `mailto` links; any other URL is dropped. */
export const safeUrl = (url: string): string => (SAFE_SCHEME.test(url.trim()) ? url : '')

const components: Components = {
	a: ({ href, children }) =>
		href ? (
			<a href={href} className="text-ds-primary underline" rel="noreferrer noopener" target="_blank">
				{children}
			</a>
		) : (
			<span>{children}</span>
		),
}

/**
 * Renders a content body the way an offer shows it (organism): Markdown without
 * raw HTML, without images, and with links only for `http`, `https` and
 * `mailto` (TO-420 content-admin-ui REQ-004).
 */
export function MarkdownPreviewView({ markdown, label }: MarkdownPreviewViewProps): ReactElement {
	return (
		<section
			aria-label={label}
			className="flex flex-col gap-2 text-ds-base text-ds-text [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
		>
			<Markdown
				remarkPlugins={[htmlAsText]}
				disallowedElements={['img']}
				urlTransform={safeUrl}
				components={components}
			>
				{markdown}
			</Markdown>
		</section>
	)
}
